import {
  Button,
  FormControl,
  IconButton,
  FormLabel,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMoreHorizontal } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  NegotiationRoundStatus,
  SourcingEventStatus,
  useCancelSourcingEventV2Mutation,
  useCloseSourcingEventV2Mutation,
  useCompleteSourcingEventV2Mutation,
  useCreateNegotiationRoundV2Mutation,
  useDeleteSourcingEventMutation,
  useReopenCompletedSourcingEventV2Mutation,
  type SourcingEventV2Dto,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { urls } from "../../../urls";
import { useCopyProcurement } from "../../sourcing-events/useCopyProcurement";
import { EventChangeEditor } from "./changes/EventChangeEditor";
import { useChangeFlow } from "./changes/useChangeFlow";
import { useRoundSelection } from "./rounds/RoundSelectionContext";
import { supportsNegotiationRounds } from "./rounds/supportsNegotiationRounds";

type ReasonAction = "cancel" | "close";

export const EventLifecycleMenu = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const navigate = useNavigate();
  const confirmationModal = useConfirmationModal();

  const [cancelEvent, { isLoading: isCancelling }] = useCancelSourcingEventV2Mutation();
  const [closeEvent, { isLoading: isClosing }] = useCloseSourcingEventV2Mutation();
  const [completeEvent] = useCompleteSourcingEventV2Mutation();
  const [reopenEvent] = useReopenCompletedSourcingEventV2Mutation();
  const [deleteEvent] = useDeleteSourcingEventMutation();
  const { copy: copyProcurement, isCopying } = useCopyProcurement();

  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);
  const [reason, setReason] = useState("");
  const change = useChangeFlow(event);

  const status = event.status;
  const isDraft = status === SourcingEventStatus.Draft;
  const canCancel =
    status === SourcingEventStatus.Published ||
    status === SourcingEventStatus.Evaluation ||
    status === SourcingEventStatus.Evaluated ||
    status === SourcingEventStatus.AwardDecision;
  const canClose = canCancel || status === SourcingEventStatus.Awarded;
  const deadline = event.eventData.bidSubmissionDeadline;
  const deadlinePassed = deadline != null && new Date(deadline).getTime() <= Date.now();
  const canComplete =
    (status === SourcingEventStatus.Published && deadlinePassed) ||
    status === SourcingEventStatus.Evaluation ||
    status === SourcingEventStatus.Awarded;
  const canReopen = status === SourcingEventStatus.Evaluated || status === SourcingEventStatus.Completed;

  // ADR-033 D1: round creation lives here now that negotiation is no longer a tab.
  const { rounds, selectRound } = useRoundSelection();
  const [createRound, { isLoading: isCreatingRound }] = useCreateNegotiationRoundV2Mutation();
  const hasDraftRound = rounds.some((round) => round.status === NegotiationRoundStatus.Draft);
  const hasFinalRound = rounds.some((round) => round.isFinalRound === true);
  // A buyer who announced a forhandlingsforbehold may award on the initial tenders, but nothing
  // stops them negotiating anyway — the reservation is a right, not a commitment.
  const canCreateRound =
    supportsNegotiationRounds(event) &&
    (status === SourcingEventStatus.Published || status === SourcingEventStatus.Evaluation) &&
    !hasDraftRound &&
    !hasFinalRound;

  const handleCreateRound = async () => {
    // The round extends the competition: default its deadline 14 days past the later of
    // now and the current final stage (event deadline or the last round's deadline).
    const stageDeadlines = [event.eventData.bidSubmissionDeadline, ...rounds.map((round) => round.deadline)]
      .filter((iso): iso is string => iso != null)
      .map((iso) => new Date(iso).getTime());
    const base = Math.max(Date.now(), ...stageDeadlines);
    const roundDeadline = new Date(base);
    roundDeadline.setDate(roundDeadline.getDate() + 14);
    try {
      const newRound = await createRound({
        eventId: event.id,
        createNegotiationRoundV2Request: {
          deadline: roundDeadline.toISOString(),
          isFinalRound: null,
        },
      }).unwrap();
      selectRound(newRound);
      navigate(urls.v2.sourcingEvents.editNegotiationRound.go(event.id, newRound.id));
    } catch (e) {
      trigger(e);
    }
  };

  const submitReason = async () => {
    const value = reason.trim() === "" ? null : reason.trim();
    try {
      if (reasonAction === "cancel") {
        await cancelEvent({
          eventId: event.id,
          cancelSourcingEventV2Request: { reason: value },
        }).unwrap();
      } else if (reasonAction === "close") {
        await closeEvent({
          eventId: event.id,
          closeSourcingEventV2Request: { reason: value },
        }).unwrap();
      }
      setReasonAction(null);
      setReason("");
    } catch (e) {
      trigger(e);
    }
  };

  const completesEvaluation = status === SourcingEventStatus.Evaluation;
  const reopensEvaluation = status === SourcingEventStatus.Evaluated;

  const handleComplete = () => {
    confirmationModal.show({
      title: completesEvaluation ? t("Complete the evaluation?") : t("Complete the procurement?"),
      description: completesEvaluation
        ? t("The scores and the price selection are frozen. You can reopen it later.")
        : t("The procurement is marked as completed. You can reopen it later."),
      onConfirmed: async () => {
        try {
          await completeEvent({ eventId: event.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleReopen = () => {
    confirmationModal.show({
      title: reopensEvaluation ? t("Reopen the evaluation?") : t("Reopen the procurement?"),
      description: t("The procurement is moved back to evaluation."),
      onConfirmed: async () => {
        try {
          await reopenEvent({ eventId: event.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleCopy = () => copyProcurement(event.id);

  const handleDelete = () => {
    confirmationModal.show({
      title: t("Delete the draft?"),
      description: t("The draft is permanently removed."),
      confirmButtonColor: "red",
      confirmButtonText: t("Delete") ?? "",
      onConfirmed: async () => {
        try {
          await deleteEvent({ eventId: event.id }).unwrap();
          navigate(urls.v2.sourcingEvents.viewAll);
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <>
      {confirmationModal.modal}
      {change.draft && change.editor.isOpen && (
        <EventChangeEditor
          key={change.draft.id}
          event={event}
          draft={change.draft}
          isOpen={change.editor.isOpen}
          onClose={change.editor.onClose}
        />
      )}
      <Modal isOpen={reasonAction != null} onClose={() => setReasonAction(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {reasonAction === "cancel" ? t("Cancel the procurement?") : t("Close the procurement?")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>{t("Reason")}</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("Shared with the invited suppliers") ?? undefined}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap="3">
            <Button variant="ghost" onClick={() => setReasonAction(null)}>
              {t("Abort")}
            </Button>
            <Button
              colorScheme="red"
              onClick={submitReason}
              isLoading={isCancelling || isClosing}
            >
              {reasonAction === "cancel" ? t("Cancel the procurement") : t("Close the procurement")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Menu>
        <MenuButton
          as={IconButton}
          size="sm"
          variant="outline"
          aria-label={t("Actions") ?? "Actions"}
          icon={<Icon as={FiMoreHorizontal} />}
        />
        <MenuList>
          {(change.canStartChange || change.draft) && (
            <MenuItem onClick={change.draft ? change.openChange : change.startChange} isDisabled={change.isStarting}>
              {change.actionLabel}
            </MenuItem>
          )}
          <MenuItem onClick={handleCopy} isDisabled={isCopying}>
            {t("Copy the procurement")}
          </MenuItem>
          {canCreateRound && (
            <MenuItem onClick={handleCreateRound} isDisabled={isCreatingRound}>
              {t("New round")}
            </MenuItem>
          )}
          {canComplete && (
            <MenuItem onClick={handleComplete}>
              {completesEvaluation ? t("Complete the evaluation") : t("Complete the procurement")}
            </MenuItem>
          )}
          {canReopen && (
            <MenuItem onClick={handleReopen}>
              {reopensEvaluation ? t("Reopen the evaluation") : t("Reopen the procurement")}
            </MenuItem>
          )}
          {canClose && <MenuItem onClick={() => setReasonAction("close")}>{t("Close the procurement")}</MenuItem>}
          {canCancel && (
            <MenuItem color="smError" onClick={() => setReasonAction("cancel")}>
              {t("Cancel the procurement")}
            </MenuItem>
          )}
          {isDraft && (
            <MenuItem color="smError" onClick={handleDelete}>
              {t("Delete the draft")}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
};
